/**
 * The speed, twice: in figures a person reads at a glance, on a card beside the measures, and
 * as a chart under the timeline they can point at.
 *
 * The chart sits under the timeline, as wide as it, on the same axis, folds and all — so a
 * moment on the chart is directly under the same moment on the timeline, and the two
 * playheads make one line. Its speeds are labelled inside the plot, at the left, rather than
 * in a margin beside it, because a margin would push the plot out of line with the timeline.
 *
 * It is drawn at its own size in pixels — measured, not stretched — so its labels are text at
 * the size of the rest of the page rather than a picture of text squeezed to fit. Hovering
 * reads the speed at a moment, and a click jumps the replay there.
 *
 * The area under the line is coloured like the map — to the client, waiting, client on
 * board — so a dip to nothing is visibly the wait at the pickup and not a mystery; speeding
 * is drawn in red over it, above the firm's limit where the car was — the town's, or the
 * open road's — which is dashed and labelled where it is drawn, and steps at the town's edge.
 */

import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useTenantFormats } from '../shell/formats';
import { Card, CardHeader, Pill, Sub, cx } from '../ui';
import {
  carAt,
  fromAxis,
  limitRuns,
  rideLimits,
  speedStats,
  speedTicks,
  toAxis,
  type Axis,
  type JourneyDetail,
} from './journey';

/**
 * The plot inside the chart: as wide as the timeline above it, with room for the top speed's
 * label above and the times below.
 */
const PLOT = { left: 0, right: 0, top: 24, bottom: 22 } as const;

/** A halo in the card's colour, so a label stays readable over the lines drawn behind it. */
const HALO = { paintOrder: 'stroke', strokeWidth: 4, strokeLinejoin: 'round' } as const;

const PHASE_FILL: Record<string, string> = {
  approach: 'fill-info/15',
  waiting: 'fill-warn/20',
  aboard: 'fill-ok/15',
};

const PHASE_STROKE: Record<string, string> = {
  approach: 'stroke-info',
  waiting: 'stroke-warn',
  aboard: 'stroke-ok',
};

/** The size of an element, kept up to date. */
function useSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;

    if (element === null) return;

    const observer = new ResizeObserver(([entry]) => {
      const box = entry.contentRect;
      // Unrounded, so the chart's playhead lands on the timeline's to the fraction of a pixel.
      setSize({ width: box.width, height: box.height });
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return [ref, size] as const;
}

/** The figures: top speed and when, the average while moving, over the limit, standing still. */
export function JourneySpeed({
  journey,
  onSeek,
  className,
}: {
  journey: JourneyDetail;
  /** Jump the replay to a real moment. */
  onSeek: (moment: number) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const stats = useMemo(() => speedStats(journey.legs), [journey.legs]);

  const top = stats.topKmh == null ? null : Math.round(stats.topKmh);
  const topAt = stats.topAt;

  return (
    <Card className={cx('flex flex-col', className)}>
      <CardHeader title={t('reports.journeys.speed.title')}>
        <Pill soft tone="danger">
          {limitWords(t, journey)}
        </Pill>
      </CardHeader>

      {!hasSpeed(journey) ? (
        <p className="m-0 px-4 py-3 text-table text-ink-3">{t('reports.journeys.speed.none')}</p>
      ) : (
        <dl className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-x-4 gap-y-3 px-4 py-3 m-0">
          <Figure
            label={t('reports.journeys.speed.top')}
            value={top == null ? '—' : t('reports.journeys.player.speed', { speed: top })}
            sub={
              topAt == null
                ? undefined
                : t('reports.journeys.speed.at', {
                    time: formats.timeWithSeconds(new Date(topAt).toISOString()),
                  })
            }
            tone={
              top != null && stats.topLimitKmh != null && top > stats.topLimitKmh
                ? 'text-danger'
                : undefined
            }
            onClick={topAt == null ? undefined : () => onSeek(topAt)}
          />
          <Figure
            label={t('reports.journeys.speed.average')}
            value={
              stats.averageMovingKmh == null
                ? '—'
                : t('reports.journeys.player.speed', {
                    speed: Math.round(stats.averageMovingKmh),
                  })
            }
            sub={t('reports.journeys.speed.averageSub')}
          />
          <Figure
            label={t('reports.journeys.speed.over')}
            value={
              stats.overLimitSeconds > 0
                ? formats.duration(stats.overLimitSeconds)
                : t('reports.journeys.speed.never')
            }
            tone={stats.overLimitSeconds > 0 ? 'text-danger' : 'text-ok'}
          />
          <Figure
            label={t('reports.journeys.speed.still')}
            value={formats.duration(stats.stillSeconds)}
            sub={t('reports.journeys.speed.stillSub')}
          />
        </dl>
      )}
    </Card>
  );
}

/**
 * The limits this ride was judged by, said with where they apply: in town, out of town, or
 * both when it crossed the edge.
 */
function limitWords(
  t: (key: string, options?: Record<string, unknown>) => string,
  journey: JourneyDetail,
) {
  const { inTownSpeedKmh: town, outOfTownSpeedKmh: road } = journey.thresholds;
  const used = rideLimits(journey.legs);

  if (town === road) return t('reports.journeys.speed.limit', { speed: town });
  if (used.length > 1) return t('reports.journeys.speed.limits', { town, road });

  return used[0] === road
    ? t('reports.journeys.speed.limitRoad', { speed: road })
    : t('reports.journeys.speed.limitTown', { speed: town });
}

function hasSpeed(journey: JourneyDetail) {
  return journey.legs.some((leg) => leg.points.some((p) => p.speedKmh != null));
}

/** One figure on a card: a label, the value in the section size, and a line under it. */
export function Figure({
  label,
  value,
  sub,
  tone,
  onClick,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: string;
  onClick?: () => void;
}) {
  return (
    <div className="min-w-0">
      <dt className="m-0 text-table text-ink-3">{label}</dt>
      <dd className={cx('m-0 text-section font-semibold tabular-nums', tone)}>
        {onClick ? (
          <button type="button" className="cursor-pointer hover:underline" onClick={onClick}>
            {value}
          </button>
        ) : (
          value
        )}
      </dd>
      {sub && <Sub block>{sub}</Sub>}
    </div>
  );
}

/** The speed as a chart, under the timeline and as wide as it. Nothing when none was recorded. */
export function SpeedChart({
  journey,
  axis,
  real,
  onSeek,
}: {
  journey: JourneyDetail;
  axis: Axis;
  /** The playhead, in real milliseconds. */
  real: number;
  /** Jump the replay to a real moment. */
  onSeek: (moment: number) => void;
}) {
  if (!hasSpeed(journey)) return null;

  return <Chart journey={journey} axis={axis} real={real} onSeek={onSeek} />;
}

function Chart({
  journey,
  axis,
  real,
  onSeek,
}: {
  journey: JourneyDetail;
  axis: Axis;
  real: number;
  onSeek: (moment: number) => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const [box, size] = useSize<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const limits = useMemo(() => limitRuns(journey.legs), [journey.legs]);
  const used = rideLimits(journey.legs);
  const topKmh = useMemo(() => speedStats(journey.legs).topKmh, [journey.legs]);
  const ticks = speedTicks(topKmh, Math.max(...used));
  const max = ticks[ticks.length - 1];

  const width = Math.max(size.width, 1);
  const height = Math.max(size.height, 1);
  const plotW = Math.max(width - PLOT.left - PLOT.right, 1);
  const plotH = Math.max(height - PLOT.top - PLOT.bottom, 1);

  const x = (moment: number) =>
    PLOT.left + (toAxis(axis, moment) / Math.max(axis.length, 1)) * plotW;
  const y = (kmh: number) => PLOT.top + plotH - (Math.min(kmh, max) / max) * plotH;

  // Runs of one phase, each an area and a line; a reading with no speed breaks the run.
  const runs = useMemo(() => {
    type Reading = { at: number; kmh: number; limit: number };
    const out: { phase: string; points: Reading[] }[] = [];

    for (const leg of journey.legs) {
      let current: (typeof out)[number] | null = null;

      for (const point of leg.points) {
        if (point.speedKmh == null) {
          current = null;
          continue;
        }

        const at = Date.parse(point.at);

        if (current === null || current.phase !== point.phase) {
          // Joined to the run before, so a change of colour does not leave a gap.
          const joint: Reading[] =
            current !== null ? [current.points[current.points.length - 1]] : [];
          const reading = { at, kmh: point.speedKmh, limit: point.limitKmh };

          current = { phase: point.phase, points: [...joint, reading] };
          out.push(current);
        } else {
          current.points.push({ at, kmh: point.speedKmh, limit: point.limitKmh });
        }
      }
    }

    return out;
  }, [journey.legs]);

  // Every chapter's start is a time worth printing under the chart; the ones that would
  // overlap the one before are left out, except the end, which takes the place of the one
  // before it instead.
  const times: number[] = [];

  for (const moment of [
    ...new Set([axis.start, ...journey.chapters.map((c) => Date.parse(c.from)), axis.end]),
  ].sort((a, b) => a - b)) {
    if (times.length === 0 || x(moment) - x(times[times.length - 1]) >= 64) times.push(moment);
    else if (moment === axis.end && times.length > 1) times[times.length - 1] = moment;
  }

  const at = (event: ReactMouseEvent<Element>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const share = Math.min(Math.max((event.clientX - rect.left - PLOT.left) / plotW, 0), 1);

    return fromAxis(axis, share * axis.length);
  };

  // The limit line, a step wherever the car crossed the town's edge: each run from where it
  // began to where the next one does, the first from the left edge, the last to the right.
  const steps = limits.map((run, i) => ({
    limit: run.limitKmh,
    from: i === 0 ? PLOT.left : x(run.from),
    to: i === limits.length - 1 ? width - PLOT.right : x(limits[i + 1].from),
  }));
  const limitPath = steps
    .map((step, i) =>
      i === 0
        ? `M${step.from.toFixed(1)},${y(step.limit).toFixed(1)} H${step.to.toFixed(1)}`
        : `V${y(step.limit).toFixed(1)} H${step.to.toFixed(1)}`,
    )
    .join(' ');

  const now = carAt(journey.legs, real);
  const hovered = hover === null ? null : carAt(journey.legs, hover);
  const time = (moment: number) => formats.time(new Date(moment).toISOString());

  return (
    <div ref={box} className="relative h-[158px]">
      {size.width > 0 && (
        <svg
          width={width}
          height={height}
          className="absolute inset-0 block cursor-crosshair select-none"
          role="img"
          aria-label={t('reports.journeys.speed.chart', { speed: used.join(' / ') })}
          onPointerMove={(event) => setHover(at(event))}
          onPointerLeave={() => setHover(null)}
          onClick={(event) => onSeek(at(event))}
        >
          {/* The speeds' lines; their numbers are drawn last, over the speed. */}
          {ticks.map((tick) => (
            <line
              key={tick}
              x1={PLOT.left}
              x2={width - PLOT.right}
              y1={y(tick)}
              y2={y(tick)}
              className={tick === 0 ? 'stroke-rule' : 'stroke-rule-soft'}
            />
          ))}

          {/* The chapters, as faint lines with their times underneath. */}
          {times.map((moment) => (
            <g key={moment}>
              <line
                x1={x(moment)}
                x2={x(moment)}
                y1={PLOT.top}
                y2={PLOT.top + plotH}
                className="stroke-rule-soft"
              />
              <text
                x={x(moment)}
                y={height - 8}
                // Centred on its line, except at the edges, where it would run off.
                textAnchor={
                  x(moment) - 24 < 0 ? 'start' : x(moment) + 24 > width ? 'end' : 'middle'
                }
                className="fill-ink-3 text-secondary tabular-nums"
              >
                {time(moment)}
              </text>
            </g>
          ))}

          {/* The speed, as a shaded area and a line, in the colour of each part of the ride. */}
          {runs.map((run, i) => {
            const line = run.points
              .map((p) => `${x(p.at).toFixed(1)},${y(p.kmh).toFixed(1)}`)
              .join(' ');
            const first = run.points[0];
            const last = run.points[run.points.length - 1];
            const base = y(0).toFixed(1);

            return (
              <g key={i}>
                <polygon
                  points={`${x(first.at).toFixed(1)},${base} ${line} ${x(last.at).toFixed(1)},${base}`}
                  className={PHASE_FILL[run.phase] ?? 'fill-info/15'}
                />
                <polyline
                  points={line}
                  fill="none"
                  strokeWidth={2}
                  strokeLinejoin="round"
                  className={PHASE_STROKE[run.phase] ?? 'stroke-info'}
                />
              </g>
            );
          })}

          {/* Above the limit, in red over the line. */}
          {runs.flatMap((run, i) =>
            run.points.slice(1).map((p, j) => {
              const previous = run.points[j];

              return previous.kmh > previous.limit || p.kmh > p.limit ? (
                <line
                  key={`${i}-${j}`}
                  x1={x(previous.at)}
                  y1={y(previous.kmh)}
                  x2={x(p.at)}
                  y2={y(p.kmh)}
                  strokeWidth={3}
                  strokeLinecap="round"
                  className="stroke-danger"
                />
              ) : null;
            }),
          )}

          {/* The firm's limit, dashed across and stepping at the town's edge; its labels are
              drawn last. */}
          <path d={limitPath} fill="none" strokeDasharray="6 4" className="stroke-danger" />

          {/* The playhead, and the speed at it — from the chart's top edge, where the
              timeline's leaves off, so the two are one line. */}
          <line
            x1={x(real)}
            x2={x(real)}
            y1={0}
            y2={PLOT.top + plotH}
            strokeWidth={2}
            className="stroke-ink"
          />
          {now?.speedKmh != null && (
            <circle
              cx={x(real)}
              cy={y(now.speedKmh)}
              r={4.5}
              strokeWidth={2}
              className="fill-surface stroke-ink"
            />
          )}

          {hover !== null && (
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={PLOT.top}
              y2={PLOT.top + plotH}
              strokeDasharray="2 3"
              className="stroke-ink-3"
            />
          )}

          {/* The speeds, numbered inside the plot just above their lines; the top one says km/h.
              One the limit's line would run through is left out: the limit says its own. */}
          {ticks.slice(1).map((tick, i) =>
            used.some((limit) => Math.abs(y(tick) - 8 - y(limit)) < 10) ? null : (
              <text
                key={tick}
                x={PLOT.left + 4}
                y={y(tick) - 4}
                {...HALO}
                className="fill-ink-3 stroke-surface text-secondary tabular-nums"
              >
                {i === ticks.length - 2 ? `${tick} ${t('reports.journeys.speed.unit')}` : tick}
              </text>
            ),
          )}

          {/* Each limit's label last, over the playhead, at the end of its part of the line —
              where that part is long enough to hold it; the card's pill says the rest. */}
          {steps.map((step, i) =>
            steps.length > 1 && step.to - step.from < 150 ? null : (
              <text
                key={i}
                x={step.to - 4}
                y={y(step.limit) - 5}
                textAnchor="end"
                {...HALO}
                className="fill-danger stroke-surface text-secondary font-semibold"
              >
                {t('reports.journeys.speed.limit', { speed: step.limit })}
              </text>
            ),
          )}
        </svg>
      )}

      {hover !== null && (
        <div
          className="absolute top-1 px-2 py-1 bg-surface border border-rule rounded-control shadow-float text-table tabular-nums pointer-events-none whitespace-nowrap"
          style={{ left: Math.min(Math.max(x(hover) - 60, 4), width - 140) }}
        >
          {formats.timeWithSeconds(new Date(hover).toISOString())}
          {hovered?.speedKmh != null &&
            ` · ${t('reports.journeys.player.speed', { speed: Math.round(hovered.speedKmh) })}`}
        </div>
      )}
    </div>
  );
}
